import api from '../utils/api';
import { normalizeApiResponse } from '../utils/apiResponseHandler';

const invitationService = {
  // Admin Dataroom Invitations APIs (for regular admin users)
  // Create dataroom invitation - removed redundant /api prefix
  create: async (dataroomUuid, data) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/invitations`, data);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // List dataroom invitations
  list: async (dataroomUuid, params = {}) => {
    try {
      const response = await api.get(`/datarooms/${dataroomUuid}/invitations`, { params });
      return normalizeApiResponse(response, 'invitations');
    } catch (error) {
      throw error;
    }
  },

  // Get single invitation
  get: async (dataroomUuid, invitationId) => {
    try {
      const response = await api.get(`/datarooms/${dataroomUuid}/invitations/${invitationId}`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Update invitation
  update: async (dataroomUuid, invitationId, data) => {
    try {
      const response = await api.put(`/datarooms/${dataroomUuid}/invitations/${invitationId}`, data);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Delete/Revoke invitation
  delete: async (dataroomUuid, invitationId) => {
    try {
      const response = await api.delete(`/datarooms/${dataroomUuid}/invitations/${invitationId}`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Resend invitation email
  resend: async (dataroomUuid, invitationId) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/invitations/${invitationId}/resend`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // User Invitation APIs (for invited users)
  // Get pending invitations for current user
  getPending: async () => {
    try {
      const response = await api.get('/invitations/pending');
      return normalizeApiResponse(response, 'invitations');
    } catch (error) {
      throw error;
    }
  },

  // View invitation details by token
  viewInvitation: async (token) => {
    try {
      const response = await api.get(`/invitations/${token}`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Accept invitation (for invited users)
  accept: async (token) => {
    try {
      const response = await api.post(`/invitations/${token}/accept`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Decline invitation (for invited users)  
  decline: async (token) => {
    try {
      const response = await api.post(`/invitations/${token}/decline`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Reject invitation (for invited users) - backward compatibility
  reject: async (token) => {
    try {
      const response = await api.post(`/invitations/${token}/reject`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Get invitation details by token
  getByToken: async (token) => {
    try {
      const response = await api.get(`/invitations/${token}`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Super Admin APIs for managing all invitations
  admin: {
    // List all invitations across all datarooms - removed /api prefix
    listAll: async (params = {}) => {
      try {
        const response = await api.get('/admin/invitations', { params });
        return normalizeApiResponse(response, 'invitations');
      } catch (error) {
        throw error;
      }
    },

    // Get invitation statistics
    getStats: async () => {
      try {
        const response = await api.get('/admin/invitations/stats');
        return normalizeApiResponse(response);
      } catch (error) {
        throw error;
      }
    },

    // Get invitations by dataroom
    getByDataroom: async (dataroomUuid, params = {}) => {
      try {
        const response = await api.get(`/admin/datarooms/${dataroomUuid}/invitations`, { params });
        return normalizeApiResponse(response, 'invitations');
      } catch (error) {
        throw error;
      }
    },

    // Delete any invitation
    delete: async (invitationId) => {
      try {
        const response = await api.delete(`/admin/invitations/${invitationId}`);
        return normalizeApiResponse(response);
      } catch (error) {
        throw error;
      }
    },

    // Resend any invitation
    resend: async (invitationId) => {
      try {
        const response = await api.post(`/admin/invitations/${invitationId}/resend`);
        return normalizeApiResponse(response);
      } catch (error) {
        throw error;
      }
    },

    // Bulk operations
    bulkDelete: async (invitationIds) => {
      try {
        const response = await api.post('/admin/invitations/bulk-delete', { invitation_ids: invitationIds });
        return normalizeApiResponse(response);
      } catch (error) {
        throw error;
      }
    },

    bulkResend: async (invitationIds) => {
      try {
        const response = await api.post('/admin/invitations/bulk-resend', { invitation_ids: invitationIds });
        return normalizeApiResponse(response);
      } catch (error) {
        throw error;
      }
    },

    // Super Admin Dataroom Invitation Management
    createInvitation: async (dataroomUuid, data) => {
      try {
        const response = await api.post(`/admin/datarooms/${dataroomUuid}/invitations`, data);
        return normalizeApiResponse(response);
      } catch (error) {
        throw error;
      }
    },

    getInvitation: async (dataroomUuid, invitationId) => {
      try {
        const response = await api.get(`/admin/datarooms/${dataroomUuid}/invitations/${invitationId}`);
        return normalizeApiResponse(response);
      } catch (error) {
        throw error;
      }
    },

    updateInvitation: async (dataroomUuid, invitationId, data) => {
      try {
        const response = await api.put(`/admin/datarooms/${dataroomUuid}/invitations/${invitationId}`, data);
        return normalizeApiResponse(response);
      } catch (error) {
        throw error;
      }
    },

    deleteInvitation: async (dataroomUuid, invitationId) => {
      try {
        const response = await api.delete(`/admin/datarooms/${dataroomUuid}/invitations/${invitationId}`);
        return normalizeApiResponse(response);
      } catch (error) {
        throw error;
      }
    },

    resendInvitation: async (dataroomUuid, invitationId) => {
      try {
        const response = await api.post(`/admin/datarooms/${dataroomUuid}/invitations/${invitationId}/resend`);
        return normalizeApiResponse(response);
      } catch (error) {
        throw error;
      }
    }
  }
};

export default invitationService;